const ROMAN_NUMERALS = [
  [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
  [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
  [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];

function toRomanNumeral(number) {
  let remaining = number;
  let result = '';

  for (const [value, symbol] of ROMAN_NUMERALS) {
    while (remaining >= value) {
      result += symbol;
      remaining -= value;
    }
  }

  return result;
}

class ImageGallery extends HTMLElement {
  constructor() {
    super();
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    this.lightboxContent = this.querySelector('.gallery__lightbox-content');
    this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
    this.lightboxNumber = this.querySelector('[data-gallery-lightbox-number]');
    this.lightboxHeading = this.querySelector('[data-gallery-lightbox-heading]');
    this.lightboxText = this.querySelector('[data-gallery-lightbox-text]');
    this.lightboxDetails = this.querySelector('[data-gallery-lightbox-details]');
    this.triggers = Array.from(this.querySelectorAll('[data-gallery-trigger]'));
    this.closeTargets = Array.from(this.querySelectorAll('[data-gallery-close]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.activeTrigger = null;
  }

  connectedCallback() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onTriggerClick);
    });
    this.closeTargets.forEach((el) => {
      el.addEventListener('click', this.close);
    });
    // The content wrapper covers the full viewport (same as the backdrop,
    // just on top of it) so the card can be centred in it, which means the
    // backdrop itself never actually receives clicks — this is what
    // catches a click in the gutter around the card. Only close when the
    // click lands on the wrapper itself, not one bubbling up from the card.
    this.lightboxContent?.addEventListener('click', this.onContentClick);
    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });
    this.closeTargets.forEach((el) => {
      el.removeEventListener('click', this.close);
    });
    this.lightboxContent?.removeEventListener('click', this.onContentClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  onTriggerClick = (event) => {
    this.showItem(event.currentTarget);
    this.lockScroll();
  };

  // The shared `.scroll-locked` class (plain overflow: hidden on html/body)
  // doesn't reliably preserve scroll position across browsers — some
  // engines snap it to 0 the moment overflow becomes hidden. Pinning the
  // body at its current offset via position: fixed keeps the page frozen
  // exactly where it was, then scrollTo restores it on close.
  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this.scrollY || 0);
  }

  // Moves to the previous/next gallery item relative to the one currently
  // shown, wrapping around at either end.
  step(direction) {
    if (!this.activeTrigger) return;

    const currentIndex = this.triggers.indexOf(this.activeTrigger);
    if (currentIndex === -1) return;

    const nextIndex = (currentIndex + direction + this.triggers.length) % this.triggers.length;
    this.showItem(this.triggers[nextIndex]);
  }

  showItem(trigger) {
    if (!this.lightbox || !this.lightboxImage) return;

    this.activeTrigger = trigger;

    // The global fade-in.js watcher only fires once per image, on page
    // load or DOM insertion — it already marked this src-less img loaded
    // immediately, since an img with no src is instantly "complete". Reset
    // and re-listen here so the fade plays again for every image shown.
    this.lightboxImage.classList.remove('is-loaded');
    this.lightboxImage.addEventListener('load', this.onLightboxImageLoad, { once: true });
    this.lightboxImage.src = trigger.dataset.lightboxSrc;
    this.lightboxImage.alt = trigger.dataset.lightboxAlt || '';

    if (this.lightboxNumber) {
      const index = Number(trigger.dataset.lightboxIndex);
      this.lightboxNumber.textContent = index > 0 ? toRomanNumeral(index) : '';
    }
    if (this.lightboxHeading) this.lightboxHeading.textContent = trigger.dataset.lightboxHeading || '';
    // These come from richtext block settings, rendered with the `escape`
    // filter into the attribute (so the browser unescapes it back to real
    // markup on parse) — trusted merchant content, not user input.
    if (this.lightboxText) this.lightboxText.innerHTML = trigger.dataset.lightboxText || '';
    if (this.lightboxDetails) this.lightboxDetails.innerHTML = trigger.dataset.lightboxDetails || '';

    this.lightbox.hidden = false;
  }

  onContentClick = (event) => {
    if (event.target === this.lightboxContent) this.close();
  };

  onLightboxImageLoad = () => {
    requestAnimationFrame(() => {
      this.lightboxImage.classList.add('is-loaded');
    });
  };

  close = () => {
    if (!this.lightbox || this.lightbox.hidden) return;

    this.lightbox.hidden = true;
    this.unlockScroll();
    this.activeTrigger?.focus();
  };

  handleKeydown = (event) => {
    if (this.lightbox?.hidden) return;

    if (event.key === 'Escape') this.close();
    if (event.key === 'ArrowLeft') this.step(-1);
    if (event.key === 'ArrowRight') this.step(1);
  };
}

customElements.define('image-gallery', ImageGallery);
